import type { CreatorApplication, CreatorApplicationStatus } from "@coinfallsmp/shared";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { activateApplication, getApplication, reviewApplication } from "../api.js";
import StatusBadge from "../components/StatusBadge.js";

export default function ApplicationDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [application, setApplication] = useState<CreatorApplication | null>(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    const app = await getApplication(id);
    setApplication(app);
    setNotes(app.reviewNotes ?? "");
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (!application || !id) return <p className="status-message">Loading…</p>;

  async function setStatus(status: CreatorApplicationStatus) {
    setBusy(true);
    setError(null);
    try {
      await reviewApplication(id!, { status, reviewNotes: notes.trim() || null });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update");
    } finally {
      setBusy(false);
    }
  }

  async function onActivate() {
    setBusy(true);
    setError(null);
    try {
      const creator = await activateApplication(id!);
      navigate(`/creators/${creator.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to activate");
      setBusy(false);
    }
  }

  return (
    <section className="form-page">
      <div className="page-header">
        <h1>{application.requestedUsername}</h1>
        <StatusBadge status={application.status} />
      </div>

      <div className="detail-grid">
        <div>
          <dl className="detail-list">
            <dt>Display name</dt>
            <dd>{application.displayName}</dd>
            <dt>Email</dt>
            <dd>{application.email}</dd>
            <dt>YouTube</dt>
            <dd>
              {application.youtubeUrl ? (
                <a href={application.youtubeUrl} target="_blank" rel="noreferrer">
                  {application.youtubeUrl}
                </a>
              ) : (
                "—"
              )}
            </dd>
            <dt>Minecraft username</dt>
            <dd>{application.minecraftUsername ?? "—"}</dd>
            <dt>Discord</dt>
            <dd>{application.discordHandle ?? "—"}</dd>
            <dt>Reason</dt>
            <dd style={{ whiteSpace: "pre-wrap" }}>{application.reason}</dd>
            <dt>Other links</dt>
            <dd style={{ whiteSpace: "pre-wrap" }}>{application.otherLinks ?? "—"}</dd>
            <dt>Additional info</dt>
            <dd style={{ whiteSpace: "pre-wrap" }}>{application.additionalInfo ?? "—"}</dd>
            <dt>Submitted</dt>
            <dd>{new Date(application.createdAt).toLocaleString()}</dd>
            <dt>Last reviewed</dt>
            <dd>
              {application.reviewedAt
                ? `${new Date(application.reviewedAt).toLocaleString()} by ${application.reviewedBy}`
                : "Not yet reviewed"}
            </dd>
          </dl>

          <p className="form-hint">
            A submitted channel URL or handle is not proof of ownership on its own — verify
            manually (e.g. over Discord) before approving.
          </p>
        </div>

        <div className="publish-targets">
          <h2>Review</h2>
          <label>
            Internal review notes
            <textarea
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={busy}
            />
          </label>

          {error ? <p className="form-error">{error}</p> : null}

          <div className="button-row">
            <button
              className="button secondary"
              onClick={() => setStatus("needs_info")}
              disabled={busy}
            >
              Request more info
            </button>
            <button
              className="button danger"
              onClick={() => setStatus("rejected")}
              disabled={busy}
            >
              Reject
            </button>
            <button
              className="button primary"
              onClick={() => setStatus("approved")}
              disabled={busy || application.status === "approved"}
            >
              Approve
            </button>
          </div>

          <hr />

          {application.resultingCreatorId ? (
            <p className="form-hint">
              Already activated — see the{" "}
              <Link to={`/creators/${application.resultingCreatorId}`}>creator account</Link>.
            </p>
          ) : (
            <>
              <p className="form-hint">
                Once approved, activate to create the account. You'll then need to reset its
                password from the creator's page to hand out initial login credentials.
              </p>
              <button
                className="button primary"
                onClick={onActivate}
                disabled={busy || application.status !== "approved"}
              >
                Activate account
              </button>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
